// src/components/PrimoAccessoSezione.js
// Una riga discreta che compare in cima a una sezione la PRIMA volta che quel singolo utente
// la apre — poi non si vede più, a meno che non la richiami dalla Guida.
//
// Nasce da un limite riconosciuto della Guida in sidebar: per quanto sia fatta bene, nessuno
// la apre spontaneamente — chi ha un dubbio prova a cliccare in giro, e la guida serve solo
// a chi si arrende. Questo invece arriva nel momento giusto, senza che nessuno debba cercarlo.
//
// Preferenza personale per singolo utente (documento onboardingStato/{uid}, già usato dalla
// checklist con merge: true — i campi non si sovrascrivono tra loro): due colleghi della
// stessa azienda vedono ciascuno la propria prima volta, indipendentemente.
import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { X } from "lucide-react";

// Testo per sezione — scritto per rispondere a "cosa ci faccio qui?", non per elencare
// funzionalità. Una sezione senza voce qui semplicemente non mostra nulla: meglio il
// silenzio di una spiegazione generica che non aiuta nessuno.
const SPIEGAZIONI = {
  "magazzino": "Qui arrivano tutte le richieste di materiale dai cantieri. Per ogni riga scrivi quanto hai davvero consegnato nella colonna Evaso — si salva da solo, senza confermare nulla — e «A Saldo» ti dice cosa manca ancora. Il tasto «Riepilogo materiali» somma tutto quello che resta da consegnare: comodo da stampare e mandare al fornitore.",
  "centro-acquisti": "Le richieste di nuova fornitura (NF) arrivate dai colleghi e gli ordini di acquisto (ODA) verso i fornitori. La parte sinistra di ogni riga è la richiesta originale e resta intoccabile: tu lavori sulla colonna Acquisti, in viola.",
  "nexiora-insight": "Nexiora legge da sola tutti i dati inseriti in azienda e ti mostra qui cosa merita attenzione adesso — commesse in sofferenza, scadenze vicine, anomalie. Più i dati sono aggiornati, più questa pagina è affidabile.",
  "programmazione": "Chi va dove, settimana per settimana. Scrivi il nome della commessa in una cella per assegnare una persona; il tasto destro sulla cella (o un dito tenuto premuto su tablet) apre i colori, utili per riconoscere a colpo d'occhio i cantieri.",
  "pianificazione-team": "Il carico di lavoro delle persone nel medio periodo: chi è saturo, chi ha spazio, e da quando è libero per una nuova commessa.",
  "scadenziario": "Tutte le scadenze in un posto solo — consegne, verifiche periodiche, contratti. Se una scadenza non compare qui, è perché non è stata registrata: non è Nexiora a dimenticarla.",
  "clienti": "L'anagrafica dei clienti, con lo storico economico e tutte le commesse collegate. Il numero cliente si assegna da solo, mai due volte lo stesso.",
  "dipendenti": "L'anagrafica delle persone. Il costo orario che inserisci qui è ciò che rende reale il Controllo Economico di ogni commessa: senza, i costi manodopera restano a zero.",
  "approvazione-cartellini": "I cartellini presenze inviati dagli operai, da approvare o respingere. Ogni approvazione resta tracciata con il tuo nome e la data.",
  "approvazione-note-spese": "Le note spese inviate dai dipendenti, con lo stesso flusso di approvazione tracciato dei cartellini.",
  "rientro-materiali": "Il materiale che torna dal cantiere e rientra in azienda, con il suo valore economico — quello che rientra non deve restare a costo della commessa.",
  "parco-mezzi": "I mezzi aziendali e i noleggi da fornitore. Per i noleggi, il costo cresce da solo giorno per giorno secondo le regole che imposti (weekend, festivi, sospensioni concordate).",
  "registro-noleggi": "Ogni noleggio da fornitore, con costo che si aggiorna da solo fino alla chiusura. Le gru e le piattaforme restano legate a una commessa, perché il loro costo entra nel Controllo Economico di quel cantiere.",
  "strumenti-tecnici": "81 calcolatori pronti all'uso, con una ricerca per trovarli — sezione cavo, caduta di tensione, coordinamento interruttore-cavo su tabelle CEI-UNEL, conversioni, finanza aziendale. Sono strumenti di calcolo: si usano e si chiudono, senza salvare dati (solo l'ammortamento può archiviare i piani dei beni).",
  "control-center": "La panoramica generale dell'azienda: progetti, grafici, eventi recenti e il log di tutte le modifiche fatte da chiunque.",
  "elaborazione-offerte": "Dal calcolo interno dei costi (quello che resta in casa) al documento finale per il cliente. Una volta finalizzata, l'offerta si blocca davvero — serve per poterla difendere se qualcuno la contesta.",
  "da-fatturare": "Le richieste di fatturazione dei PM all'amministrazione: ogni PM vede le sue, chi gestisce la fatturazione le vede tutte.",
  "report-ore": "Quante ore ha fatto ciascuno, su quale commessa, in che periodo — con export Excel per chi deve elaborare le buste paga.",
  "registro-cavi": "Tutti gli Ordine Cavi di ogni commessa. Un click su una riga apre direttamente quella commessa sul suo tab Ordine Cavi.",
};

export default function PrimoAccessoSezione({ sezioneId }) {
  const { currentUser } = useAuth();
  const [mostra, setMostra] = useState(false);
  const [caricato, setCaricato] = useState(false);

  const testo = SPIEGAZIONI[sezioneId];

  useEffect(() => {
    if (!currentUser?.uid || !testo) { setCaricato(true); return; }
    let annullato = false;
    getDoc(doc(db, "onboardingStato", currentUser.uid)).then(snap => {
      if (annullato) return;
      const viste = snap.exists() ? (snap.data().sezioniViste || {}) : {};
      setMostra(!viste[sezioneId]);
      setCaricato(true);
    }).catch(() => { if(!annullato) setCaricato(true); });
    return () => { annullato = true; };
  }, [currentUser?.uid, sezioneId, testo]);

  const chiudi = async () => {
    setMostra(false);
    if (!currentUser?.uid) return;
    try {
      await setDoc(doc(db, "onboardingStato", currentUser.uid), {
        uid: currentUser.uid,
        sezioniViste: { [sezioneId]: true },
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (e) { console.warn("primo accesso sezione:", e); }
  };

  if (!testo || !caricato || !mostra) return null;

  return (
    <div style={{display:"flex",gap:10,alignItems:"flex-start",background:"#faf5ff",border:"1px solid #e9d5ff",
      borderRadius:10,padding:"11px 14px",margin:"0 0 16px",fontSize:12,color:"#5b21b6",lineHeight:1.6}}>
      <span style={{fontSize:15,flexShrink:0,lineHeight:1.2}}>💜</span>
      <div style={{flex:1,minWidth:0}}>
        <strong style={{fontWeight:700}}>Prima volta qui.</strong> {testo}
      </div>
      <button onClick={chiudi} title="Ho capito, non mostrare più"
        style={{flexShrink:0,display:"flex",alignItems:"center",gap:4,background:"#fff",border:"1px solid #e9d5ff",
          borderRadius:7,padding:"4px 9px",cursor:"pointer",color:"#6d28d9",fontSize:11,fontWeight:700,fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>
        <X size={11}/> Ho capito
      </button>
    </div>
  );
}
